import { sendEvent } from "./utils";

const PENDING_LOGIN_KEY = "vc-google-analytics-pending-login";

// Covers the sign-in's own reload; "reopen closed tab" restores session storage, and must not replay it later.
const PENDING_LOGIN_MAX_AGE_MS = 2 * 60 * 1000;

// A sign-in's reload unloads the page well within this, and the timer with it. A page still here got no reload: a
// step after the token failed and the visitor stayed on the form.
const RELOAD_WAIT_MS = 10 * 1000;

type PendingLoginType = { method: string; params?: Gtag.CustomParams; savedAt: number };

// One wait, for the latest attempt: a retry restarts it, so an earlier attempt's timer cannot take the retry's login
// while its reload is still on the way.
let stalledLoginTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * A successful sign-in reloads the page right after reporting it, so a `login` sent now races the unload — and carries
 * the signed-out identity, because the new user's properties are applied by the next page. That page sends it; when no
 * reload comes, this page does, so the login is never lost.
 */
export function deferLogin(method: string, params?: Gtag.CustomParams): void {
  const pending: PendingLoginType = { method, params, savedAt: Date.now() };

  try {
    sessionStorage.setItem(PENDING_LOGIN_KEY, JSON.stringify(pending));
  } catch {
    // No storage to carry it across the reload: the old race is still better than no event at all.
    send(pending);
    return;
  }

  clearTimeout(stalledLoginTimer);
  stalledLoginTimer = setTimeout(sendStalledLogin, RELOAD_WAIT_MS);
}

/**
 * Removed before it is sent, so a login is sent at most once — by the tab that signed in (session storage is per tab),
 * and only when that page really is signed in.
 */
export function sendPendingLogin(isAuthenticated: boolean): void {
  const pending = takePendingLogin();

  if (pending && isAuthenticated) {
    send(pending);
  }
}

// Under the signed-out identity this page still has, as before the login was deferred.
function sendStalledLogin(): void {
  const pending = takePendingLogin();

  if (pending) {
    send(pending);
  }
}

function takePendingLogin(): PendingLoginType | undefined {
  let pending: PendingLoginType | undefined;

  try {
    const stored = sessionStorage.getItem(PENDING_LOGIN_KEY);
    sessionStorage.removeItem(PENDING_LOGIN_KEY);
    pending = stored ? (JSON.parse(stored) as PendingLoginType) : undefined;
  } catch {
    pending = undefined;
  }

  // A corrupt timestamp makes the age NaN, which is not fresh either.
  const isFresh = Date.now() - (pending?.savedAt ?? Number.NaN) <= PENDING_LOGIN_MAX_AGE_MS;

  return isFresh ? pending : undefined;
}

function send(pending: PendingLoginType): void {
  sendEvent("login", { ...pending.params, method: pending.method });
}
